'use client';

/* ═══════════════════════════════════════════════════
   JacquardScript — SSR cookie-first FOUC prevention (ADR-025 §7)

   Renders an inline <script> that reads Jacquard cookies
   (`jq-theme`, `jq-dir`, `jq-locale`) and applies them to
   <html> before the first paint. Must be placed in <head>
   (Next.js `_document`, Remix `root`, etc.).

   The script is entirely self-contained — no imports, no
   module system, no external dependencies. It runs before
   React hydrates, so the DOM already has the correct attributes
   when the providers mount, eliminating any flash.

   Usage (Next.js _document.tsx):
     <Head>
       <JacquardScript />
     </Head>

   IMPORTANT: This script sets attributes on <html> before React
   hydrates. To avoid hydration mismatch warnings, your <html>
   element must have suppressHydrationWarning:

     <html suppressHydrationWarning>

   Next.js 13+ adds this automatically. Remix / Vite SSR adopters
   must add it manually.
   ═══════════════════════════════════════════════════ */

export function JacquardScript() {
  // Cookie keys read inline (no module imports — see component docblock).
  // jq-density is optional; absent → no attribute is set and the kit's
  // documented defaults render. ADR-028 ambient density (codex #108 R1)
  // sets the attribute pre-paint so descendant CSS selectors match on
  // the first render — no post-hydration shift.
  /* Audit Finding #8 (round 2): every cookie value is whitelisted
     before it reaches a global attribute or window.__JACQUARD__.
     Previously `jq-theme` and `jq-dir` matched `\w+` and flowed
     straight into `data-theme` / `dir`. The whitelist alone wasn't
     enough — the original regex also matched suffix cookie names like
     `not-jq-theme=...`. Each pattern now anchors with `(?:^|;\s*)` so
     only a cookie that BEGINS with the literal `jq-<key>=` matches.
     The captured value is run through `decodeURIComponent` (wrapped in
     a try, so any single bad cookie can't break the whole pre-paint
     bootstrap), then through the per-cookie allowlist:
       jq-theme    ∈ {light, dark}                  → falls back to OS pref
       jq-dir      ∈ {ltr, rtl}                     → falls back to 'ltr'
       jq-locale   matches the BCP-47 grammar       → falls back to undefined
                   allowing 1-char singleton subtags (`-u-`, `-t-`) so
                   tags like `en-US-u-ca-buddhist` are accepted.
       jq-density  ∈ {compact, comfortable}         → falls back to null

     Helper `g(k)` extracts and decodes one cookie. Inlined as a
     single statement so the IIFE stays a single line for SSR perf. */
  const scriptContent =
    `(()=>{try{` +
    `const c=document.cookie;` +
    `const g=function(k){const m=c.match(new RegExp('(?:^|;\\\\s*)'+k+'=([^;]*)'));if(!m)return undefined;try{return decodeURIComponent(m[1])}catch(_){return m[1]}};` +
    `const tRaw=g('jq-theme');` +
    `const t=(tRaw==='light'||tRaw==='dark')?tRaw:(matchMedia('(prefers-color-scheme:dark)').matches?'dark':'light');` +
    `const dRaw=g('jq-dir');` +
    `const d=(dRaw==='ltr'||dRaw==='rtl')?dRaw:'ltr';` +
    `const lRaw=g('jq-locale');` +
    `const l=(lRaw&&/^[a-zA-Z]{2,3}(?:-[a-zA-Z0-9]{1,8})*$/.test(lRaw))?lRaw:void 0;` +
    `const dnRaw=g('jq-density');` +
    `const dn=(dnRaw==='compact'||dnRaw==='comfortable')?dnRaw:null;` +
    `const r=document.documentElement;` +
    `r.dataset.theme=t;` +
    `r.setAttribute('dir',d);` +
    `if(l)r.setAttribute('lang',l);` +
    `if(dn)r.setAttribute('data-density',dn);` +
    `window.__JACQUARD__={theme:t,dir:d,locale:l,density:dn}` +
    `}catch(e){}})();`;

  return <script dangerouslySetInnerHTML={{ __html: scriptContent }} suppressHydrationWarning />;
}
